// @ts-nocheck
import { Box, Container, Group, useMantineColorScheme } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../auth/AuthProvider";
import { CVPreviewSidebar } from "../../components/common/CVPreviewSidebar";
import { SidePanelShell } from "../../components/common/SidePanelShell";
import { SearchConfigModal } from "../../components/common/SearchConfigModal";
import { usePersonFilter } from "../../context/PersonFilterContext";
import { useSearchConfig } from "../../context/SearchConfigContext";
import { Person } from "../../models/person.model";
import { GetPersonsPageRequest } from "../../services/personService";
import { parseKeywords } from "../../utils/keywordUtils";
import { BulkOperationsManager } from "./components/bulk-operations/BulkOperationsManager";
import { deselectAllIds, selectAllIds, toggleId } from "../../utils/selectionManager";
import { CVListGrid } from "./components/CVListGrid";
import { CVListPagination } from "./components/CVListPagination";
import { CVListSearch } from "./components/CVListSearch";
import { CVListTable } from "./components/CVListTable";
import { DeleteConfirmModal } from "./components/DeleteConfirmModal";
import { SearchResultsSummary } from "./components/SearchResultsSummary";
import { useCVListActions } from "./hooks/useCVListActions";
import { useCVListData } from "./hooks/useCVListData";
import { useCVListFilters } from "./hooks/useCVListFilters";

type ViewMode = "grid" | "list";

export function CVList() {
    const navigate = useNavigate();
    const { colorScheme } = useMantineColorScheme();
    const isMobile = useMediaQuery("(max-width: 768px)");
    const isDark = colorScheme === "dark";
    const { userRole, isAuthenticated, updateUserRole } = useAuth();
    const [roleLoading, setRoleLoading] = useState(false);
    const roleLoadingRef = useRef(false);

    const { filteredPersons, filterTitle, isFiltered, clearFilter } = usePersonFilter();
    const { config: searchConfig } = useSearchConfig();

    const {
        searchQuery,
        setSearchQuery,
        availabilityFilterActive,
        setAvailabilityFilterActive,
        availabilityDays,
        setAvailabilityDays,
        inactiveFilterActive,
        setInactiveFilterActive,
        selectedLocation,
        setSelectedLocation,
        selectedLanguage,
        setSelectedLanguage,
        selectedProficiency,
        setSelectedProficiency,
        pageSize,
        pageIndex,
        setPageIndex,
        sortField,
        sortDirection,
        handleSortChange,
        handlePageSizeChange,
        handlePageChange,
    } = useCVListFilters();

    const [debouncedSearchQuery] = [searchQuery];

    const [viewMode, setViewMode] = useState<ViewMode>("grid");
    const [selectionMode, setSelectionMode] = useState(false);
    const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());

    const {
        persons,
        loading,
        totalItems,
        totalPages,
        searchError,
        searchStartTime,
        searchDuration,
        appliedFilters,
        currentKeywords,
        refetchData,
        setSearchError,
        setLoading,
        setSearchStartTime,
    } = useCVListData({
        searchQuery,
        debouncedSearchQuery,
        pageSize,
        pageIndex,
        sortField,
        sortDirection,
        inactiveFilterActive,
        availabilityFilterActive,
        availabilityDays,
        selectedLocation,
        selectedLanguage,
        selectedProficiency,
    });

    const {
        selectedPersonId,
        actionLoading,
        deleteConfirmModalOpen,
        personToDelete,
        setDeleteConfirmModalOpen,
        setPersonToDelete,
        handleSelectPerson,
        handleClosePreview,
        handleModifyPerson,
        handleActivityChange,
        handleDeletePerson,
        executeDeletePerson,
        handleCopyToProposal,
    } = useCVListActions({ refetchData });

    // Make sure user role is loaded
    useEffect(() => {
        const loadRole = async () => {
            if (!isAuthenticated || roleLoadingRef.current) return;

            try {
                setRoleLoading(true);
                roleLoadingRef.current = true;
                await updateUserRole();
            } catch (error) {
                console.error("Error loading user role:", error);
            } finally {
                setRoleLoading(false);
                roleLoadingRef.current = false;
            }
        };

        loadRole();
    }, [isAuthenticated, updateUserRole]);

    const formatRelativeTime = (date: Date | string | undefined, fallbackDate?: Date | string): string => {
        const dateToUse = date || fallbackDate;

        if (!dateToUse) return "Never updated";

        const updateDate = typeof dateToUse === 'string' ? new Date(dateToUse) : dateToUse;

        if (isNaN(updateDate.getTime()) || updateDate.getFullYear() <= 1) {
            return "Never updated";
        }

        const now = new Date();
        const diffInMs = now.getTime() - updateDate.getTime();
        const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));
        const diffInWeeks = Math.floor(diffInDays / 7);
        const diffInMonths = Math.floor(diffInDays / 30);
        const diffInYears = Math.floor(diffInDays / 365);

        if (diffInDays < 1) return "Today";
        if (diffInDays === 1) return "Yesterday";
        if (diffInDays < 7) return `${diffInDays} days ago`;
        if (diffInWeeks === 1) return "1 week ago";
        if (diffInWeeks < 4) return `${diffInWeeks} weeks ago`;
        if (diffInMonths === 1) return "1 month ago";
        if (diffInMonths < 12) return `${diffInMonths} months ago`;
        if (diffInYears === 1) return "1 year ago";
        return `${diffInYears} years ago`;
    };

    const personDisplayData = useMemo(() => {
        const sourceData = isFiltered ? filteredPersons : persons;

        return sourceData.map((person) => ({
            ...person,
            lastUpdated: formatRelativeTime(person.cvUpdateDate, person.createdDate),
        })) as (Person & { lastUpdated: string })[];
    }, [persons, filteredPersons, isFiltered]);

    const handleSearchChange = (value: string) => {
        setSearchQuery(value);
        setPageIndex(0);
    };

    const handleSearchClear = () => {
        setSearchQuery("");
        setPageIndex(0);
    };

    const handleSearchSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        refetchData();
    };

    const handleViewModeChange = (value: string) => {
        const newViewMode = value as ViewMode;
        setViewMode(newViewMode);
        localStorage.setItem("cvlist-view-mode", newViewMode);
    };

    // Build the search request for bulk export
    const buildSearchRequest = useMemo((): GetPersonsPageRequest => {
        const keywords = parseKeywords(debouncedSearchQuery);
        return {
            filterModel: {
                pageIndex,
                pageSize,
                filterKeywords: keywords,
                inactive: inactiveFilterActive,
                availableInDays: availabilityFilterActive ? 0 : (availabilityDays ?? null),
                sortField: sortField || "",
                sortDirection,
                officeLocationId: selectedLocation ? parseInt(selectedLocation) : undefined,
                matchTechThreshold: searchConfig.matchTechThreshold,
                matchCertThreshold: searchConfig.matchCertThreshold,
                maxTechMatches: searchConfig.maxTechMatches,
                maxCertMatches: searchConfig.maxCertMatches,
                exactMatchOnly: searchConfig.exactMatchOnly,
                exactTechMatchOnly: searchConfig.exactTechMatchOnly,
                exactCertMatchOnly: searchConfig.exactCertMatchOnly,
                useTechSearch: searchConfig.useTechSearch,
                useCertSearch: searchConfig.useCertSearch,
                filteringOperator: searchConfig.filteringOperator,
                languageId: selectedLanguage ?? undefined,
                proficiencyId: selectedProficiency ?? undefined,
            }
        };
    }, [
        pageIndex,
        pageSize,
        debouncedSearchQuery,
        inactiveFilterActive,
        availabilityFilterActive,
        sortField,
        sortDirection,
        selectedLocation,
        searchConfig,
        selectedLanguage,
        selectedProficiency,
    ]);

    const handleEnterSelectionMode = () => {
        setSelectionMode(true);
        setSelectedIds(new Set());
    };

    const handleExitSelectionMode = () => {
        setSelectionMode(false);
        setSelectedIds(new Set());
    };

    const handleToggleSelect = (id: number) => {
        setSelectedIds(prev => toggleId(prev, id));
    };

    const handleSelectAllOnPage = () => {
        const pageIds = personDisplayData.map(p => p.id);
        setSelectedIds(prev => selectAllIds(prev, pageIds));
    };

    const handleDeselectAllOnPage = () => {
        const pageIds = personDisplayData.map(p => p.id);
        setSelectedIds(prev => deselectAllIds(prev, pageIds));
    };

    const handleDeselectByIds = (ids: number[]) => {
        if (ids.length === 0) {
            return;
        }

        setSelectedIds(prev => deselectAllIds(prev, ids));
    };

    const handleReselectByIds = (ids: number[]) => {
        if (ids.length === 0) {
            return;
        }

        setSelectedIds(prev => selectAllIds(prev, ids));
    };

    return (
        <Container size="xl" p={0} fluid>
            <Box
                style={{ position: "relative" }}
                role="main"
                aria-label="CV search and results"
            >
                <Box
                    id="main-content"
                    p="md"
                    style={{
                        marginRight: selectedPersonId && !isMobile ? "400px" : "0",
                        transition: "margin-right 0.3s ease",
                    }}
                >
                    <CVListSearch
                        searchQuery={searchQuery}
                        onSearchChange={handleSearchChange}
                        onSearchClear={handleSearchClear}
                        onSearchSubmit={handleSearchSubmit}
                        availabilityFilterActive={availabilityFilterActive}
                        onAvailabilityFilterChange={() => {
                            setAvailabilityFilterActive((prev) => !prev);
                            setPageIndex(0);
                        }}
                        availabilityDays={availabilityDays}
                        onAvailabilityDaysChange={(days) => {
                            setAvailabilityDays(days);
                            setPageIndex(0);
                        }}
                        inactiveFilterActive={inactiveFilterActive}
                        onInactiveFilterChange={() => {
                            setInactiveFilterActive((prev) => !prev);
                            setPageIndex(0);
                        }}
                        selectedLocation={selectedLocation}
                        onLocationChange={(value) => {
                            setSelectedLocation(value);
                            setPageIndex(0);
                        }}
                        selectedLanguage={selectedLanguage}
                        onLanguageChange={(value) => {
                            setSelectedLanguage(value);
                            setPageIndex(0);
                        }}
                        selectedProficiency={selectedProficiency}
                        onProficiencyChange={(value) => {
                            setSelectedProficiency(value);
                            setPageIndex(0);
                        }}
                        isFiltered={isFiltered}
                        loading={loading}
                    />

                    <Group justify="flex-start" align="flex-start" mb={4} wrap="nowrap">
                        <Box style={{ flex: 1, minWidth: 0 }}>
                            <SearchResultsSummary
                                totalItems={totalItems}
                                isFiltered={isFiltered}
                                filteredCount={filteredPersons.length}
                                filterTitle={filterTitle}
                                onClearFilter={clearFilter}
                                appliedFilters={appliedFilters}
                                loading={loading}
                                searchDuration={searchDuration}
                                searchError={searchError}
                            />
                        </Box>
                    </Group>

                    <BulkOperationsManager
                        selectionMode={selectionMode}
                        onEnterSelectionMode={handleEnterSelectionMode}
                        currentPagePersons={personDisplayData}
                        selectedIds={selectedIds}
                        totalItems={totalItems}
                        searchRequest={buildSearchRequest}
                        onSelectAllOnPage={handleSelectAllOnPage}
                        onDeselectAllOnPage={handleDeselectAllOnPage}
                        onDeselectByIds={handleDeselectByIds}
                        onReselectByIds={handleReselectByIds}
                        onClearAll={() => setSelectedIds(new Set())}
                        onCancel={handleExitSelectionMode}
                        hasResults={totalItems > 0}
                        loading={loading}
                    />

                    {viewMode === "grid" ? (
                        <CVListGrid
                            persons={personDisplayData}
                            loading={loading}
                            searchDuration={searchDuration}
                            totalItems={totalItems}
                            sortField={sortField}
                            sortDirection={sortDirection}
                            selectedPersonId={selectedPersonId}
                            currentKeywords={currentKeywords}
                            pageSize={pageSize}
                            isFiltered={isFiltered}
                            viewMode={viewMode}
                            onSortChange={handleSortChange}
                            onPageSizeChange={handlePageSizeChange}
                            onViewModeChange={handleViewModeChange}
                            onSelectPerson={handleSelectPerson}
                            onModifyPerson={handleModifyPerson}
                            onActivityChange={handleActivityChange}
                            onDeletePerson={handleDeletePerson}
                            onCopyToProposal={handleCopyToProposal}
                            onClearSearch={handleSearchClear}
                            onRefresh={refetchData}
                            selectionMode={selectionMode}
                            selectedIds={selectedIds}
                            onToggleSelect={handleToggleSelect}
                        />
                    ) : (
                        <CVListTable
                            persons={personDisplayData}
                            loading={loading}
                            searchDuration={searchDuration}
                            sortField={sortField}
                            sortDirection={sortDirection}
                            currentKeywords={currentKeywords}
                            pageSize={pageSize}
                            isFiltered={isFiltered}
                            viewMode={viewMode}
                            onSortChange={handleSortChange}
                            onPageSizeChange={handlePageSizeChange}
                            onViewModeChange={handleViewModeChange}
                            onSelectPerson={handleSelectPerson}
                            onModifyPerson={handleModifyPerson}
                            onDeletePerson={handleDeletePerson}
                            onClearSearch={handleSearchClear}
                            onRefresh={refetchData}
                            selectionMode={selectionMode}
                            selectedIds={selectedIds}
                            onToggleSelect={handleToggleSelect}
                        />
                    )}

                    <CVListPagination
                        isFiltered={isFiltered}
                        totalPages={totalPages}
                        pageIndex={pageIndex}
                        pageSize={pageSize}
                        totalItems={totalItems}
                        filteredCount={filteredPersons.length}
                        onPageChange={handlePageChange}
                    />
                </Box>

                {selectedPersonId && (
                    <SidePanelShell onClose={handleClosePreview}>
                        <CVPreviewSidebar
                            isEditable={true}
                            personId={selectedPersonId}
                            onClose={handleClosePreview}
                            onDownloadCV={() => { }}
                        />
                    </SidePanelShell>
                )}
            </Box>

            <DeleteConfirmModal
                opened={deleteConfirmModalOpen}
                personToDelete={personToDelete}
                onClose={() => {
                    setDeleteConfirmModalOpen(false);
                    setPersonToDelete(null);
                }}
                onConfirm={executeDeletePerson}
            />

            <SearchConfigModal />
        </Container>
    );
}

